/* eslint-disable max-len */
import { isObject } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { type AdditionalDetailsSpecsFragment } from '../../../../api/fragments/AdditionalDetailsSpecs';
import { useCreateAdditionalDetailMutation } from '../../../../api/mutations/createAdditionalDetail';
import { useDeleteAdditionalDetailMutation } from '../../../../api/mutations/deleteAdditionalDetail';
import { useDeleteVariantConfiguratorPackageAdditionalDetailsAssetMutation } from '../../../../api/mutations/deleteVariantConfiguratorPackageAdditionalDetailsAsset';
import { useUpdateAdditionalDetailMutation } from '../../../../api/mutations/updateAdditionalDetail';
import { useUploadVariantConfiguratorPackageAdditionalDetailsAssetMutation } from '../../../../api/mutations/uploadVariantConfiguratorPackageAdditionalDetailsAsset';
import { VariantConfiguratorAsset } from '../../../../api/types';
import { type PackageSettingValues, type PackageAdditionalDetailValues } from './utils';

const useHandleAdditionalDetails = () => {
    const [createAdditionalDetailsMutation] = useCreateAdditionalDetailMutation();
    const [updateAdditionalDetailsMutation] = useUpdateAdditionalDetailMutation();
    const [deleteAdditionalDetailsMutation] = useDeleteAdditionalDetailMutation();

    const [uploadAdditionalDetailImage] = useUploadVariantConfiguratorPackageAdditionalDetailsAssetMutation();
    const [deleteAdditionalDetailImage] = useDeleteVariantConfiguratorPackageAdditionalDetailsAssetMutation();

    // create additional details
    const createAdditionalDetails = useCallback(
        async (
            packageSettingId: string,
            variantConfiguratorId: string,
            blockId: string,
            settings: PackageAdditionalDetailValues
        ) => {
            const result = await createAdditionalDetailsMutation({
                variables: {
                    blockId,
                    packageSettingId,
                    settings: {
                        title: settings.title,
                        description: settings.description,
                    },
                    variantConfiguratorId,
                },
            });

            if (settings.image instanceof File) {
                await uploadAdditionalDetailImage({
                    variables: {
                        blockId,
                        packageSettingId,
                        additionalDetailsId: result.data?.additionalDetails.id,
                        id: variantConfiguratorId,
                        type: VariantConfiguratorAsset.AdditionalDetailsImage,
                        upload: settings.image,
                    },
                });
            }
        },
        [createAdditionalDetailsMutation, uploadAdditionalDetailImage]
    );

    // delete additional details
    const deleteAdditionalDetails = useCallback(
        async (
            blockId: string,
            variantConfiguratorId: string,
            packageSettingId: string,
            additionalDetailId: string
        ) => {
            await deleteAdditionalDetailImage({
                variables: {
                    additionalDetailsId: additionalDetailId,
                    blockId,
                    id: variantConfiguratorId,
                    packageSettingId,
                    type: VariantConfiguratorAsset.AdditionalDetailsImage,
                },
            });

            await deleteAdditionalDetailsMutation({
                variables: {
                    additionalDetailId,
                    blockId,
                    packageSettingsId: packageSettingId,
                    variantConfiguratorId,
                },
            });
        },
        [deleteAdditionalDetailImage, deleteAdditionalDetailsMutation]
    );

    // update additional details
    const updateAdditionalDetails = useCallback(
        async (
            variantConfiguratorId: string,
            blockId: string,
            packageSetting: PackageSettingValues,
            settings: PackageAdditionalDetailValues,
            initialAdditinalDetail?: AdditionalDetailsSpecsFragment
        ) => {
            // Update additional details
            await updateAdditionalDetailsMutation({
                variables: {
                    additionalDetailId: settings.id,
                    blockId,
                    packageSettingsId: packageSetting.id,
                    variantConfiguratorId,
                    settings: {
                        description: settings.description,
                        title: settings.title,
                    },
                },
            });

            // update image if needed
            if (settings.image instanceof File) {
                await uploadAdditionalDetailImage({
                    variables: {
                        blockId,
                        additionalDetailsId: settings.id,
                        id: variantConfiguratorId,
                        packageSettingId: packageSetting.id,
                        type: VariantConfiguratorAsset.AdditionalDetailsImage,
                        upload: settings.image,
                    },
                });
            } else if (!settings.image && isObject(initialAdditinalDetail?.image)) {
                await deleteAdditionalDetailImage({
                    variables: {
                        additionalDetailsId: settings.id,
                        blockId,
                        id: variantConfiguratorId,
                        packageSettingId: packageSetting.id,
                        type: VariantConfiguratorAsset.AdditionalDetailsImage,
                    },
                });
            }
        },
        [deleteAdditionalDetailImage, updateAdditionalDetailsMutation, uploadAdditionalDetailImage]
    );

    return useMemo(
        () => ({
            createAdditionalDetails,
            deleteAdditionalDetails,
            updateAdditionalDetails,
        }),
        [createAdditionalDetails, deleteAdditionalDetails, updateAdditionalDetails]
    );
};

export default useHandleAdditionalDetails;
